import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { string, object, date, ref } from 'yup'
import { gql } from '@apollo/client'
import { format } from 'date-fns'

import client, { deserializeErrors } from 'apollo-client'
import { useSites } from 'store'
import { createSsidNameSchema } from 'utils/ssid-name'
import { createSsidPassphraseSchema } from 'utils/ssid-passphrase'

export const buildSchema = ({ t }) => {
  return object({
    label: string().required(t('ui_elements.required_field')).max(100, t('event_ssids.errors.label_max_length')),
    name: createSsidNameSchema(t),
    passphrase: createSsidPassphraseSchema(t),
    start_date: date().required(t('ui_elements.required_field')).nullable().typeError(t('ui_elements.required_field')),
    start_time: string().required(t('ui_elements.required_field')),
    end_date: date()
      .required(t('ui_elements.required_field'))
      .nullable()
      .typeError(t('ui_elements.required_field'))
      .min(ref('start_date'), t('event_ssids.errors.end_date_before_start')),
    end_time: string().required(t('ui_elements.required_field')),
    utc_offset: string().required(t('ui_elements.required_field')),
    description: string().nullable()
  })
}

const OPERATION_CREATE = gql`
  mutation CreateCmtSsid($params: CmtSsidCreateInput) {
    ssid: createCmtSsid(params: $params) {
      id
      label
      name
      description
      enabled
      start_date
      start_time
      end_date
      end_time
      utc_offset
      passphrase
      error
      created_at
      created_by {
        email
      }
      updated_by {
        email
      }
    }
  }
`

const OPERATION_UPDATE = gql`
  mutation UpdateCmtSsid($params: CmtSsidUpdateInput) {
    ssid: updateCmtSsid(params: $params) {
      id
      label
      name
      description
      enabled
      start_date
      start_time
      end_date
      end_time
      utc_offset
      passphrase
      error
      created_at
      created_by {
        email
      }
      updated_by {
        email
      }
    }
  }
`

const OPERATION_REMOVE = gql`
  mutation RemoveCmtSsid($id: ID!) {
    result: removeCmtSsid(id: $id)
  }
`

const OPERATION_DEACTIVATE = gql`
  mutation DeactivateCmtSsid($id: ID!) {
    ssid: deactivateCmtSsid(id: $id) {
      id
    }
  }
`

const OPERATION_ACTIVATE = gql`
  mutation ActivateCmtSsid($id: ID!) {
    ssid: activateCmtSsid(id: $id) {
      id
    }
  }
`

const OPERATION_GENERATE_PASSPHRASE = gql`
  mutation GeneratePassphraseCmtSsid($id: ID!) {
    ssid: generatePassphraseCmtSsid(id: $id) {
      id
      name
      label
      passphrase
      error
    }
  }
`

export const useRemove = (): [() => Promise<{ error?: string }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)
  const { id } = useParams()
  const remove = async () => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_REMOVE,
        variables: { id },
        refetchQueries: ['cmtSsids']
      })
      return {}
    } catch (e) {
      // @ts-expect-error from JSX
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || deserializeErrors(e)
      const cleanError = errorMessage.includes('::') ? errorMessage.split('::').pop().trim() : errorMessage
      return { error: cleanError }
    } finally {
      setLoading(false)
    }
  }
  return [remove, { loading }]
}

export const useDeactivate = (): [(id: string) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const deactivate = async (id: string) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_DEACTIVATE,
        variables: { id },
        refetchQueries: ['cmtSsids', 'cmtSsid']
      })
      return {}
    } catch (e) {
      // @ts-expect-error from JSX
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || deserializeErrors(e)
      const cleanError = errorMessage.includes('::') ? errorMessage.split('::').pop().trim() : errorMessage
      return { error: cleanError }
    } finally {
      setLoading(false)
    }
  }

  return [deactivate, { loading }]
}

export const useActivate = (): [(id: string) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const activate = async (id: string) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_ACTIVATE,
        variables: { id },
        refetchQueries: ['cmtSsids', 'cmtSsid']
      })
      return {}
    } catch (e) {
      // @ts-expect-error from JSX
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || deserializeErrors(e)
      const cleanError = errorMessage.includes('::') ? errorMessage.split('::').pop().trim() : errorMessage
      return { error: cleanError }
    } finally {
      setLoading(false)
    }
  }

  return [activate, { loading }]
}

export const useGeneratePassphrase = (): [
  (id: string) => Promise<{ error?: string; passphrase?: string }>,
  { loading: boolean }
] => {
  const [loading, setLoading] = useState<boolean>(false)

  const generatePassphrase = async (id: string) => {
    setLoading(true)
    try {
      const response = await client.mutate({
        mutation: OPERATION_GENERATE_PASSPHRASE,
        variables: { id },
        refetchQueries: ['cmtSsid', 'cmtSsids']
      })
      return { passphrase: response.data.ssid.passphrase }
    } catch (e) {
      // @ts-expect-error from JSX
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || deserializeErrors(e)
      const cleanError = errorMessage.includes('::') ? errorMessage.split('::').pop().trim() : errorMessage
      return { error: cleanError }
    } finally {
      setLoading(false)
    }
  }

  return [generatePassphrase, { loading }]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useSave = (): [(any) => Promise<{ error?: string; data?: any }>, { loading: boolean; error: string | null }] => {
  const [{ selected }] = useSites()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execMutation = async ({
    id,
    label,
    name,
    passphrase,
    description,
    start_date,
    start_time,
    end_date,
    end_time,
    utc_offset
  }) => {
    let response
    const formattedData = {
      label,
      name,
      passphrase,
      description: description || null,
      start_date: format(start_date, 'yyyy-MM-dd'),
      start_time,
      end_date: format(end_date, 'yyyy-MM-dd'),
      end_time,
      utc_offset
    }

    if (id) {
      response = await client.mutate({
        mutation: OPERATION_UPDATE,
        variables: {
          params: {
            id,
            ...formattedData
          }
        },
        refetchQueries: ['cmtSsids', 'cmtSsid']
      })
    } else {
      response = await client.mutate({
        mutation: OPERATION_CREATE,
        variables: {
          params: {
            site_sf_id: selected[0].sf_id,
            ...formattedData
          }
        },
        refetchQueries: ['cmtSsids']
      })
    }
    return response.data.ssid
  }

  const save = async values => {
    setLoading(true)
    setError(null)
    try {
      const ssid = await execMutation(values)
      return { data: ssid }
    } catch (e) {
      console.error('Save error:', e)

      // @ts-expect-error from JSX
      let errorMessage = e.graphQLErrors?.[0]?.message || e.message || deserializeErrors(e)

      if (errorMessage.includes('::')) {
        errorMessage = errorMessage.split('::').pop().trim()
      }

      setError(errorMessage)
      return { error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  return [save, { loading, error }]
}

export default useSave
